// Shared types and constants used by both the Web Worker and the main thread service.

// Maps signal name suffix → MotorSignals field
export const MOTOR_SIGNAL_SUFFIXES: Record<string, keyof MotorSignals> = {
  '_TorqueRmsValue':                  'torqueRms',
  '_TorqueDcValue':                   'torqueDc',
  '_LagDistanceDcValue':              'lagDistanceDc',
  '_LagDistanceRmsValue':             'lagDistanceRms',
  '_TempMotor':                       'tempMotor',
  '_TempAmplifier':                   'tempAmplifier',
  '_DiagMsg':                         'diagMsg',
  '_FB_Hours':                        'fbHours',
  '_FB_Days':                         'fbDays',
  '_FB_Minutes':                      'fbMinutes',
  '_FB_acthours':                     'fbActHours',
  '_MotorPartNo':                     'motorPartNo',
  '_MotorTyp':                        'motorTyp',
  '_MasterVelAtStartTorqueAnalysis':  'velAtStart',
  '_MasterVelAtStopTorqueAnalysis':   'velAtStop',
  '_etCamIdentifierActive':           'camActive',
  '_etCamIdentifierNext':             'camNext',
};

export interface MotorSignals {
  torqueRms?: number;
  torqueDc?: number;
  lagDistanceDc?: number;
  lagDistanceRms?: number;
  tempMotor?: number;
  tempAmplifier?: number;
  ambientTemperature?: number;
  velocity?: number;
  diagMsg?: string;
  fbHours?: number;
  fbDays?: number;
  fbMinutes?: number;
  fbActHours?: number;
  motorPartNo?: string;
  motorTyp?: string;
  velAtStart?: number;
  velAtStop?: number;
  camActive?: string;
  camNext?: string;
}

export interface MotorHistoryPoint {
  timestamp: number;
  torqueRms?: number;
  torqueDc?: number;
  lagDistanceDc?: number;
  lagDistanceRms?: number;
  tempMotor?: number;
  tempAmplifier?: number;
  ambientTemperature?: number;
  velocity?: number;
  fbActHours?: number;
}

export interface MotorState {
  id: string;        // e.g. "P0655_F_ZIFO_MA12"
  shortName: string; // e.g. "MA12"
  signals: MotorSignals;
  history: MotorHistoryPoint[];
  lastUpdate: number;
}

// ─── Messages: Main → Worker ─────────────────────────────────────────────────

export interface WorkerConnectMsg {
  type: 'CONNECT';
  serverUrl: string;
  token: string;
  /**
   * Optional NATS `user` to present with the token ("demo" in demo mode).
   * Workers cannot read the settings store, so it is passed in.
   */
  natsUser?: string;
  /** NATS subject wildcard, e.g. "focke-test.live.99999.*" */
  subject: string;
  /** Stream name to subscribe via JetStream ordered consumer (live updates) */
  streamName: string;
  /**
   * Optional "current value" stream + subject (e.g. FOCKE_TEST_CURRENT,
   * `edge-data.current.*.<serial>.>`). When provided, the discovery pass reads
   * the last value per subject from here instead of the live stream, so the
   * list shows the latest value even when a signal hasn't published within the
   * live stream's retention window. Live updates still use `subject`/`streamName`.
   */
  currentSubject?: string;
  currentStreamName?: string;
  /** ISO-8601 string — replay from this point; omit for live-only */
  startTime?: string;
  /**
   * Minimum milliseconds between stored history points during JetStream replay.
   * Undefined = full resolution (2 s default). Use larger values for wide ranges:
   * 1d → 2 min, 3d → 5 min, 1w → 15 min, 2w → 30 min.
   */
  sampleIntervalMs?: number;
  /**
   * Worker operating mode:
   * - 'live'    — discovery pass then deliverNew consumer; always responsive
   * - 'history' — replay from startSequence with batched processing; posts
   *               HISTORY_BATCH messages and exits when caught up
   * When omitted defaults to 'live' for backward compatibility.
   */
  mode?: 'live' | 'history';
  /**
   * When set in history mode, only process signals belonging to this motor.
   * Signal names are matched as `motorId + suffix` (e.g. "P0655_F_ZIFO_MA12_TorqueRmsValue").
   * Dramatically reduces CPU and memory by skipping all other motors' data.
   */
  motorId?: string;
  /**
   * Motor-specific NATS subject prefix for server-side filtering (new format only).
   * e.g. "focke-test.live.22622.P0655.F.ZIFO.MA12"
   * When set, history/detail subscriptions use `motorSubjectPrefix.>` instead of
   * the broad wildcard, so the server only delivers that motor's messages.
   */
  motorSubjectPrefix?: string;
  /**
   * Skip the full-stream replay phase. List-only consumers (motor cards /
   * servo tree) need just the latest value per axis (discovery + deliverNew);
   * chart history is loaded from Doris on demand instead of replaying the
   * whole stream.
   */
  skipReplay?: boolean;
}

export interface WorkerDisconnectMsg {
  type: 'DISCONNECT';
}

/**
 * Request the worker to fetch full-resolution data for a specific time window.
 * The worker responds with one or more RANGE_DETAIL_DATA messages.
 */
export interface WorkerLoadRangeDetailMsg {
  type: 'LOAD_RANGE_DETAIL';
  /** ISO-8601 start of the range to load at full resolution */
  startIso: string;
  /** ISO-8601 end of the range to load at full resolution */
  endIso: string;
  /** Only load detail data for this motor (skip all other signals) */
  motorId?: string;
  /** Motor-specific NATS subject prefix (new format) for server-side filtering */
  motorSubjectPrefix?: string;
}

/** Tell the live worker which motor is selected (for chart history). null = none. */
export interface WorkerSetSelectedMotorMsg {
  type: 'SET_SELECTED_MOTOR';
  motorId: string | null;
  /** Motor-specific NATS subject prefix (new format). Used for detail loads. */
  motorSubjectPrefix?: string;
}

export type MainToWorkerMsg = WorkerConnectMsg | WorkerDisconnectMsg | WorkerLoadRangeDetailMsg | WorkerSetSelectedMotorMsg;

// ─── Messages: Worker → Main ─────────────────────────────────────────────────

export interface WorkerConnectedMsg {
  type: 'CONNECTED';
}

export interface WorkerDisconnectedMsg {
  type: 'DISCONNECTED';
  error?: string;
}

/** Lightweight motor entry sent in snapshots — no history array (too expensive to clone) */
export interface MotorSnapshotEntry {
  id: string;
  shortName: string;
  signals: MotorSignals;
  lastUpdate: number;
  /** Only the latest history point for live chart addPoint — null if unchanged */
  latestPoint: MotorHistoryPoint | null;
}

export interface WorkerSnapshotMsg {
  type: 'MOTORS_SNAPSHOT';
  motors: [string, MotorSnapshotEntry][];
  /** True while replaying historical messages, false once caught up to live */
  replaying: boolean;
  /** 0-100 replay progress estimate (based on time range) */
  replayProgress: number;
}

/** Sent once after CONNECTED with stream retention info */
export interface WorkerStreamInfoMsg {
  type: 'STREAM_INFO';
  /** ISO-8601 timestamp of the oldest message in the stream */
  firstMessageTime: string | null;
  /** ISO-8601 timestamp of the newest message */
  lastMessageTime: string | null;
}

export interface WorkerErrorMsg {
  type: 'ERROR';
  error: string;
}

/**
 * Sent after a LOAD_RANGE_DETAIL request completes.
 * Contains full-resolution history points for the requested time window,
 * keyed by motor ID. The main thread merges these into its chart data
 * without touching the overview motor.history.
 */
export interface WorkerRangeDetailMsg {
  type: 'RANGE_DETAIL_DATA';
  patches: Array<{ motorId: string; points: MotorHistoryPoint[] }>;
}

/**
 * Sent periodically by the history worker during replay.
 * Contains batches of history points that the service merges into
 * the motor state maintained by the live worker.
 */
export interface WorkerHistoryBatchMsg {
  type: 'HISTORY_BATCH';
  /** Map entries: [motorId, historyPoints[]] */
  patches: Array<{ motorId: string; points: MotorHistoryPoint[] }>;
  /** True while still replaying, false when this is the final batch */
  replaying: boolean;
  /** 0-100 progress estimate */
  replayProgress: number;
}

/** Sent by the history worker when it has finished replaying all history */
export interface WorkerHistoryDoneMsg {
  type: 'HISTORY_DONE';
}

/** Sent after the discovery pass with discovered motor IDs and their subject prefixes */
export interface WorkerDiscoveryMsg {
  type: 'DISCOVERY';
  /** Detected subject format: 'old' = flat (4 tokens), 'new' = dot-separated (>4 tokens) */
  format: 'old' | 'new';
  motors: Array<{
    id: string;
    shortName: string;
    /** NATS subject prefix for this motor (new format only, empty for old) */
    subjectPrefix: string;
  }>;
}

/** Debug log forwarded from worker to main thread (worker console.log is invisible in some browsers) */
export interface WorkerDebugLogMsg {
  type: 'DEBUG_LOG';
  message: string;
}

export type WorkerToMainMsg =
  | WorkerConnectedMsg
  | WorkerDisconnectedMsg
  | WorkerSnapshotMsg
  | WorkerStreamInfoMsg
  | WorkerRangeDetailMsg
  | WorkerHistoryBatchMsg
  | WorkerHistoryDoneMsg
  | WorkerDiscoveryMsg
  | WorkerErrorMsg
  | WorkerDebugLogMsg;
